import { FJDecodeMode, FJDecoderManager, FJEncodeMode, FJEncoderManager, FJWASMLoader } from '@fj/codec';
import FJConfig from '../../config/FJConfig.js';
import FJConstant from '../../constants/FJConstant.js';
import { TEXT_BASE_EDITING_SOURCE_TYPE } from '../../redux/slices/FJSliceRightTextBaseEditing.js';
import FJTextBaseEditingUtil from '../../util/FJTextBaseEditingUtil.js';
import FJUtil from '../../util/FJUtil.js';
import FJAudioPlayBase from '../FJAudio/FJAudioPlayBase.js';
import FJVideoPlayBase from '../FJVideo/FJVideoPlayBase.js';
import { renderThumbnails } from '../../store/FJVideoThumbnailStore.js';
import WaveSurfer from 'wavesurfer.js';
import FJPoolStore from '../../store/FJPoolStore.js';
import FJVideoUtil from '../../util/FJVideoUtil.js';
import FJAudioUtil from '../../util/FJAudioUtil.js';

let _currentExportId = '';

class FJTextBaseEditingPreviewManager {
    _sourceType = TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO;

    _viewCanvas = null;
    _viewCtx = null;
    _rect = {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
    };
    _splitTimeList = [];

    _videoPlayer = null;
    _audioPlayer = null;

    // 视频开始时长
    _startTime = 0;
    // 视频结束时长
    _endTime = 0;

    _isPlaying = false;

    // 时间线播放时长 视频是的当前时长 - 视频开始时长
    _timeLineCurrentTime = 0;

    constructor() {}

    async init(url, type) {
        this._sourceType = type;

        switch (type) {
            case TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO:
                this._videoPlayer = new FJVideoPlayBase();
                this._videoPlayer.clearAll();
                await this._videoPlayer.setSource(url);
                this.initVideoRect();
                break;
            case TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO:
                this._audioPlayer = new FJAudioPlayBase();
                this._audioPlayer.clearAll();
                await this._audioPlayer.setSource(url);
                break;
        }
    }

    getSourceInfo() {
        switch (this._sourceType) {
            case TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO:
                return this._videoPlayer.getSourceInfo();
            case TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO:
                return this._audioPlayer.getSourceInfo();
        }
    }

    /**
     * @desc 更新缩略图 并返回缩略图链接
     * @returns {Promise<string>}
     */
    async updateThumbnailBlobUrl(thumbnailSize) {
        switch (this._sourceType) {
            case TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO:
                return await this._videoUpdateThumbnailBlobUrl(thumbnailSize);
            case TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO:
                return await this._audioUpdateThumbnailBlobUrl(thumbnailSize);
        }
    }

    async _imageBitmapToBlob(imageBitmap) {
        // 创建一个 OffscreenCanvas 或 HTMLCanvasElement
        const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height);
        const context = canvas.getContext('2d');

        // 将 ImageBitmap 绘制到 canvas 上
        context.drawImage(imageBitmap, 0, 0);

        // 使用 canvas 的 toBlob 方法获取 Blob
        return new Promise((resolve, reject) => {
            canvas.convertToBlob().then(resolve).catch(reject);
        });
    }

    async getThumbnailBlobUrlByFirstFrame(thumbnailSize, callback) {
        const sourceUrl = this._videoPlayer.getSourceUrl();
        if (!sourceUrl) return;

        const video = document.createElement('video');
        video.src = sourceUrl;

        const offscreenCanvas = new OffscreenCanvas(thumbnailSize.width, thumbnailSize.height);

        const __onCanplay = () => {
            video.currentTime = 0;
            video.removeEventListener('canplay', __onCanplay);
        };

        const __onSeek = () => {
            video.removeEventListener('seeked', __onSeek);
            const sourceSize = this._videoPlayer.getSize();
            const thumbnailItemSize = {
                width: Math.floor(thumbnailSize.height * (sourceSize.width / sourceSize.height)),
                height: thumbnailSize.height,
            };

            const ctx = offscreenCanvas.getContext('2d');
            const num = Math.ceil(thumbnailSize.width / thumbnailItemSize.width);
            for (let i = 0; i < num; i++) {
                const x = i * thumbnailItemSize.width;
                const y = 0;
                ctx.drawImage(video, x, y, thumbnailItemSize.width, thumbnailItemSize.height);
            }

            offscreenCanvas.convertToBlob().then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                callback && callback(blobUrl);
            });
        };

        video.addEventListener('canplay', __onCanplay);

        video.addEventListener('seeked', __onSeek);
    }

    async _videoUpdateThumbnailBlobUrl(thumbnailSize) {
        const sourceBlob = await this._videoPlayer.getSourceBlob();

        if (!sourceBlob) return;
        const sourceSize = this._videoPlayer.getSize();
        const thumbnailItemSize = {
            width: Math.floor(thumbnailSize.height * (sourceSize.width / sourceSize.height)),
            height: thumbnailSize.height,
        };

        let worker = new Worker(FJConfig.videoThumbnailWorkerPath);
        const readyPromise = new Promise((resolve, reject) => {
            worker.onmessage = e => {
                const msg = e.data;

                if (msg.type === 'ready') {
                    resolve();
                } else if (msg.type === 'error') {
                    console.error(msg.message);
                    reject(new Error(msg.message));
                } else if (msg.type === 'done') {
                    // const ctx = this._videoThumbnailCanvas.getContext('2d');
                    // ctx.drawImage(msg.imageBitMap, 0, 0);
                    // worker.terminate();
                } else {
                    reject(new Error('Not Ready'));
                }
            };
        });

        await readyPromise;
        const wasm = await new Promise(resolve => FJWASMLoader.loadWasmBinary(resolve));

        const offscreenCanvas = new OffscreenCanvas(thumbnailSize.width, thumbnailSize.height);

        const file = new File([sourceBlob], 'video.mp4', { type: 'video/mp4' });
        worker.postMessage(
            {
                wasm,
                offscreenCanvas,
                thumbnailSize: thumbnailItemSize,
                file: file,
                renderInfo: {
                    rotation: 0,
                    flipX: 1,
                    flipY: 1,
                    relativeRect: {
                        x: 0,
                        y: 0,
                        width: 1,
                        height: 1,
                    },
                },
                // 使用获取到的视频信息
                fileInfo: { width: sourceSize.width, height: sourceSize.height },
                start: this._startTime,
                end: this._endTime,
                type: 'RENDER_TUNMBNAILS',
                wasmResourceVersion: FJConfig.codecVersion,
            },
            [offscreenCanvas],
        );

        try {
            return await new Promise((resolve, reject) => {
                worker.onmessage = async e => {
                    const msg = e.data;
                    if (msg.type === 'done') {
                        const blob = await this._imageBitmapToBlob(msg.imageBitMap);
                        const blobUrl = URL.createObjectURL(blob);

                        worker.terminate();
                        msg.imageBitMap?.close(); // 安全关闭
                        resolve(blobUrl);
                    } else if (msg.type === 'error') {
                        // 增加错误处理细节
                        worker.terminate();
                        reject(new Error(msg.message));
                    }
                };
            });
        } catch (error) {
            worker.terminate();
            const sourceUrl = this._videoPlayer.getSourceUrl();
            if (!sourceUrl) return;

            if (this._thumbnailAbortController) {
                this._thumbnailAbortController.abort();
            }
            const canvas = FJPoolStore.getCanvas('2d', true);
            canvas.width = thumbnailSize.width;
            canvas.height = thumbnailSize.height;

            await renderThumbnails(sourceUrl, canvas, {
                thumbnailSize: thumbnailItemSize,
                renderInfo: null,
                fileInfo: { width: sourceSize.width, height: sourceSize.height },
                start: this._startTime,
                end: this._endTime,
                setAbortController: abortController => {
                    this._thumbnailAbortController = abortController;
                },
            });

            const blob = await canvas.convertToBlob();
            const blobUrl = URL.createObjectURL(blob);
            FJPoolStore.releaseCanvas(canvas);
            return blobUrl;
        }
    }

    async _audioUpdateThumbnailBlobUrl(thumbnailSize) {
        const sourceUrl = this._audioPlayer.getSourceUrl();
        if (!sourceUrl) return;

        let waveDom = document.createElement('div');
        waveDom.style.setProperty('width', `${thumbnailSize.width}px`);
        waveDom.style.setProperty('height', `${thumbnailSize.height}px`);
        document.body.appendChild(waveDom);

        try {
            const waveSurfer = WaveSurfer.create({
                container: waveDom,
                height: thumbnailSize.height * 2,
                width: thumbnailSize.width,
                waveColor: '#4cb490',
                interact: false,
                barWidth: 1,
                barGap: 2,
                cursorWidth: 0,
            });

            return await new Promise((resolve, reject) => {
                waveSurfer.on('ready', () => {
                    waveSurfer.exportImage('image/png', 1).then(async res => {
                        resolve(res[0]);
                        document.body.removeChild(waveDom);
                        waveDom = null;
                    });
                });

                waveSurfer.on('error', e => {
                    reject(e);
                    document.body.removeChild(waveDom);
                    waveDom = null;
                });
                waveSurfer.load(sourceUrl);
            });
        } catch (error) {
            console.error(error);
            document.body.removeChild(waveDom);
            return '';
        }
    }

    /**
     * @desc seek
     * @param {number} time
     */
    async seek(time) {
        this._timeLineCurrentTime = time;

        let sourceCurrentTime = FJTextBaseEditingUtil.timeLineCurrentTimeToSourceCurrentTime(
            this._timeLineCurrentTime,
            this._splitTimeList,
            this._startTime,
        );

        switch (this._sourceType) {
            case TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO:
                await this._videoPlayer.seek(sourceCurrentTime, () => {
                    this.drawVideoCanvas();
                });
                break;
            case TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO:
                await this._audioPlayer.seek(sourceCurrentTime);
                break;
        }
    }

    /**
     * @desc 设置视频画布
     * @param {HTMLCanvasElement} canvas
     */
    setVideoCanvas(canvas) {
        this._viewCanvas = canvas;
        this._viewCtx = canvas.getContext('2d');

        this.initVideoRect();
    }

    /**
     * @desc 暂停
     */
    async pause() {
        if (!this._isPlaying) return;

        switch (this._sourceType) {
            case TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO:
                this._videoPlayer.pause();
                break;
            case TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO:
                this._audioPlayer.pause();
                break;
        }

        this._isPlaying = false;
        if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
    }

    /**
     * @desc 播放
     */
    play(callback, pauseCallback) {
        if (this._isPlaying) return;
        this._isPlaying = true;

        let requestAnimationTime;

        // 计算总播放时长
        const allPlayTime = this._splitTimeList.reduce((total, item) => {
            return total + (item.endTime - item.startTime);
        }, 0);

        const __callback = sourceCurrentTime => {
            const currentTime = FJTextBaseEditingUtil.timeLineCurrentTimeToSourceCurrentTime(
                this._timeLineCurrentTime,
                this._splitTimeList,
                this._startTime,
            );

            if (!FJUtil.compareTimes(sourceCurrentTime, currentTime, 0.1)) {
                this.seek(this._timeLineCurrentTime);
            }

            callback && callback(this._timeLineCurrentTime);

            if (this._sourceType === TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO) {
                this.drawVideoCanvas();
            }
        };

        const __pauseCallback = () => {
            this._isPlaying && this.pause();
            pauseCallback && pauseCallback(this._timeLineCurrentTime);
            if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
        };

        switch (this._sourceType) {
            case TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO:
                this._videoPlayer.play(__callback, __pauseCallback);
                break;
            case TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO:
                this._audioPlayer.play(__callback, __pauseCallback);
                break;
        }

        let __playing = timestamp => {
            if (!requestAnimationTime) requestAnimationTime = timestamp;

            this._timeLineCurrentTime += (timestamp - requestAnimationTime) / 1000;
            requestAnimationTime = timestamp;

            if (this._timeLineCurrentTime >= allPlayTime) {
                this._isPlaying && this.pause();
                pauseCallback && pauseCallback(this._timeLineCurrentTime);
            } else if (this._isPlaying) {
                this._requestTimer = window.requestAnimationFrame(__playing);
            }
        };

        if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
        this._requestTimer = window.requestAnimationFrame(__playing);
    }

    /**
     * @desc 初始化适配渲染矩形
     */
    initVideoRect() {
        const isLoaded = this._videoPlayer.getSourceStatus() === FJConstant.SUCCESS;

        if (!isLoaded || !this._viewCanvas) return;

        const domSize = this._viewCanvas.getBoundingClientRect();
        this._viewCanvas.width = domSize.width;
        this._viewCanvas.height = domSize.height;

        const sourceSize = this._videoPlayer.getSize();

        this._rect = {
            width: sourceSize.width,
            height: sourceSize.height,
        };

        if (sourceSize.width > domSize.width || sourceSize.height > domSize.height) {
            const scale = Math.min(domSize.width / sourceSize.width, domSize.height / sourceSize.height);
            this._rect.width = sourceSize.width * scale;
            this._rect.height = sourceSize.height * scale;
        }

        this._rect.x = (domSize.width - this._rect.width) / 2;
        this._rect.y = (domSize.height - this._rect.height) / 2;
    }

    getSourceCurrentTime() {
        switch (this._sourceType) {
            case TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO:
                return this._videoPlayer.getCurrentTime();
            case TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO:
                return this._audioPlayer.getCurrentTime();
        }
    }

    /**
     * @desc 绘制视频画布
     */
    drawVideoCanvas() {
        if (!this._viewCanvas || !this._viewCtx) return;
        this._viewCtx.clearRect(0, 0, this._viewCanvas.width, this._viewCanvas.height);

        // 文案全部删除完 不渲染
        if (this._splitTimeList.length === 0) {
            return;
        }

        this._videoPlayer.draw(this._viewCtx, this._rect);
    }

    /**
     * @desc 设置分割时间列表
     * @param {Array} splitTimeList
     */
    setSplitTimeList(splitTimeList) {
        this._splitTimeList = splitTimeList;
    }

    /**
     * @desc 设置时间数据
     * @param {number} startTime
     * @param {number} endTime
     */
    setTimeData(startTime, endTime) {
        this._startTime = startTime;
        this._endTime = endTime;
    }

    /**
     * @desc 清除
     */
    clear() {
        this._videoPlayer.clearAll();
        this._audioPlayer.clearAll();
    }

    /**
     * @desc 合并导出
     */
    exportMerged(options) {
        switch (this._sourceType) {
            case TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO:
                this._exportMergedVideo(options);
                break;
            case TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO:
                this._exportMergedAudio(options);
                break;
        }
    }

    createExportCanvas() {
        if (!this._exportCanvas) {
            this._exportCanvas = FJPoolStore.getCanvas('2d');
            this._exportCtx = this._exportCanvas.getContext('2d', { willReadFrequently: true });
        }

        return { exportCanvas: this._exportCanvas, exportCtx: this._exportCtx };
    }

    /**
     * @desc 合并导出视频
     */
    async _exportMergedVideo(options) {
        const { onProgress, onComplete, onError } = options;

        const decodeStart = this._startTime;
        const decodeEnd = this._endTime;
        const file = await this._videoPlayer.getSourceBlob();

        const { exportCanvas, exportCtx } = this.createExportCanvas();

        (async () => {
            const audioConfig = {
                bitrate: 256 * 1000,

                samplerate: FJUtil.getAudioSampleRate(),
            };

            // TODO fj-codec 修复 importScripts 后可以删除
            /**
             * 目前断网情况下 importScripts codec.js 的错误没有正确处理
             * 所以这里使用 setTimeout 配合 navigator.onLine 来判断是否断网
             * 如果断网就抛出异常，因为断网情况下如果 codec.js 没有缓存的话获取视频信息会卡住
             */
            const { width, height, hasAudio, hasVideo, rotate } = await Promise.race([
                FJDecoderManager.getVideoInfo(file),
                new Promise((resolve, reject) => {
                    setTimeout(() => {
                        if (!navigator.onLine) {
                            reject(new Error('No internet connection'));
                        }
                    }, 3000);
                }),
            ]);

            if (!hasVideo) {
                onError(new Error('No video found'));
                return;
            }

            // 导出gif时，帧率固定为10, 视频时使用源视频帧率 gif 输出尺寸设置为 最长边480px
            // 导出视频时，帧率固定在30帧
            const FPS = 30;
            const { width: fitWidth, height: fitHeight } = { width, height };

            exportCanvas.width = fitWidth;
            exportCanvas.height = fitHeight;

            const videoConfig = {
                fps: FPS,
                width: rotate === 90 || rotate === 270 ? fitHeight : fitWidth,
                height: rotate === 90 || rotate === 270 ? fitWidth : fitHeight,
                presetIdx: 0, // h264编码参数，控制输出速度和质量。0代表最快，0-7依次对应"ultrafast", "superfast", "veryfast", "faster", "fast", "medium", "slow", "veryslow"
                codecIdx: 1, // 编码器索引，0代表vpx，1代表h264, 2代表gif
                formatIdx: 1, // 格式索引，0代表webm，1代表mp4, 2代表gif

                loop: 0, // GIF编码参数，0代表无限循环播放, 1代表播放一次
                mode: FJEncodeMode.WEBCODECS,
                quality: {
                    crf: 20,
                    bitrate: 10000000,
                },

                bitrateMode: 'variable', // h264编码参数，控制输出速度和质量。constant代表恒定码率，variable代表可变码率
                exportQuality: 1080,
                concurrentSize: 2,
            };

            await new Promise(resolve => {
                const encoderOptions = {
                    initCallback: resolve,
                    doneCallback: data => {
                        const blob = new Blob([data], { type: 'video/mp4' });
                        onComplete(blob, TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO);
                        FJEncoderManager.cancel();
                    },
                    chunkCallback: () => {},

                    errorCallback: onError,
                    encodeCallback: () => {},
                };
                FJEncoderManager.initEncoder(videoConfig, audioConfig, encoderOptions);
            });

            if (hasAudio) {
                FJEncoderManager.markAudioEncode(true);
            }

            const config = {
                fileName: file.name,
                width: fitWidth,
                height: fitHeight,
                start: decodeStart,
                end: decodeEnd,
                speed: 1,
                fps: FPS,
                samplerate: FJUtil.getAudioSampleRate(),
                mode: FJDecodeMode.FFMPEG,
                frameType: FJVideoUtil.getDecodeVideoFrameType(),
                ignoreNoValidDuration: true,
            };

            let count = 0;
            let useCount = 0;
            let timestampPerFrame = (1000 * 1000) / FPS;
            let maxCount = 0;
            if (decodeEnd === Infinity) {
                const { duration: decodeDuration } = await FJDecoderManager.getVideoInfo(file, {
                    ignoreNoValidDuration: true,
                });
                let duration = decodeDuration;
                if (duration <= 0) {
                    duration = (await FJDecoderManager.getVideoInfoByElement(file))?.duration || Infinity;
                }
                maxCount = (duration - decodeStart) * FPS;
            } else {
                maxCount = (decodeEnd - decodeStart) * FPS;
            }

            // 判断时间是否有效
            const __isInValidTime = (start, end) => {
                if (!this._splitTimeList || this._splitTimeList.length === 0) {
                    return true;
                }

                const inVideoStart = this._startTime + start;
                const inVideoEnd = this._startTime + end;

                const isInValidTime = this._splitTimeList.some(item => {
                    return inVideoStart >= item.startTime && inVideoEnd <= item.endTime;
                });

                return isInValidTime;
            };

            // 解码视频
            await new Promise((resolve, reject) => {
                FJDecoderManager.decodeVideo(file, config, {
                    decodeCallback: frame => {
                        try {
                            const { data, error, ended } = frame;
                            if (error) {
                                reject(error);
                                return;
                            }

                            if (ended) {
                                FJEncoderManager.markVideoFrameDecodeDone();
                                resolve();
                                return;
                            }

                            const { frameWidth, frameHeight, data: imageSourceFromData, start, end } = data;

                            if (!__isInValidTime(start, end)) {
                                count++;
                                onProgress(count / maxCount);
                                return;
                            }

                            exportCanvas.width = 1;
                            exportCanvas.height = 1;

                            exportCanvas.width = frameWidth;
                            exportCanvas.height = frameHeight;

                            exportCtx.clearRect(0, 0, frameWidth, frameHeight);
                            exportCtx.drawImage(imageSourceFromData, 0, 0, frameWidth, frameHeight);

                            imageSourceFromData?.getContext &&
                                imageSourceFromData?.getContext('2d')?.clearRect(0, 0, frameWidth, frameHeight);

                            let imageSource = exportCanvas;

                            const couldOptFrame = FJUtil.couldOptFrame();
                            let frameType = 'VIDEOFRAME';
                            let vf = null;

                            if (
                                typeof VideoFrame === 'undefined' ||
                                FJUtil.getBrowser() === 'FF' ||
                                FJUtil.getBrowser() === 'Safari'
                            ) {
                                vf = couldOptFrame
                                    ? FJUtil.getImageBitmap(exportCanvas)
                                    : exportCtx.getImageData(0, 0, exportCanvas.width, exportCanvas.height);
                                frameType = couldOptFrame ? 'IMAGEBITMAP' : 'IMAGEDATA';
                                count++;
                                useCount++;
                            } else {
                                const sourceWidth = frameWidth;
                                const sourceHeight = frameHeight;

                                if (frameType === 'VIDEOFRAME') {
                                    count++;
                                    // eslint-disable-next-line no-undef
                                    vf = new VideoFrame(exportCanvas, {
                                        // format: 'RGBX',
                                        alpha: 'discard',
                                        displayWidth: sourceWidth,
                                        displayHeight: sourceHeight,
                                        duration: timestampPerFrame,
                                        timestamp: timestampPerFrame * ++useCount,
                                    });
                                }

                                if (frameType === 'IMAGEDATA') {
                                    count++;
                                    useCount++;
                                    vf = exportCtx.getImageData(0, 0, frameWidth, frameHeight);
                                }
                            }

                            FJEncoderManager.addVideoFrame({ type: frameType, data: vf });
                            imageSource.close && imageSource.close();
                            onProgress(count / maxCount);
                        } catch (error) {
                            reject(error);
                        }
                    },
                    getValidFramesSize: () => {
                        return FJEncoderManager.getValidFramesSize();
                    },
                });
            });

            // 编码音频
            if (hasAudio) {
                const audioData = await FJDecoderManager.decodeAudioOverall(file, {
                    start: decodeStart,
                    end: decodeEnd,
                    samplerate: FJUtil.getAudioSampleRate(),
                    mode: FJDecodeMode.FFMPEG,
                    speed: 1,
                    fileType: 'video',
                    ignoreNoValidDuration: true,
                });

                const segments = this._splitTimeList.map(item => ({
                    start: FJAudioUtil.timeToSamples(item.startTime, FJUtil.getAudioSampleRate()),
                    end: FJAudioUtil.timeToSamples(item.endTime, FJUtil.getAudioSampleRate()),
                }));
                const newAudioData = FJAudioUtil.extractMultiSegments(audioData, segments);

                FJEncoderManager.markAudioEncode(false);
                FJEncoderManager.addAudioChunk(newAudioData.buffer, true);
            }
        })()
            .catch(e => {
                onError(e);
            })
            .finally(() => {
                this.removeExportCanvas();
            });
    }

    removeExportCanvas() {
        if (!this._exportCanvas) return;
        FJPoolStore.releaseCanvas(this._exportCanvas);
        this._exportCanvas = null;
        this._exportCtx = null;
    }

    /**
     * @desc 合并导出音频
     */
    async _exportMergedAudio(options) {
        const { onComplete, onError, onProgress } = options;
        const id = new Date().getTime();
        _currentExportId = id;

        try {
            // 在处理过程中检查是否被中断
            const __checkAborted = () => {
                if (_currentExportId && _currentExportId !== id) {
                    throw new Error('ignore');
                } else if (!_currentExportId) {
                    throw new Error('Export cancelled');
                }
            };

            const source = await this._audioPlayer.getSourceBlob();
            __checkAborted();
            onProgress && onProgress(0.2);
            const audioData = await FJDecoderManager.decodeAudioOverall(source, {
                start: this._startTime,
                end: this._endTime,
                samplerate: FJUtil.getAudioSampleRate(),
                mode: FJDecodeMode.FFMPEG,
                speed: 1,
                fileType: 'audio',
                ignoreNoValidDuration: true,
            });
            __checkAborted();

            // 假进度
            const l = 0.8 / 10;
            let t = 0.2 + l;
            for (let i = 0; i < 10; i++) {
                onProgress && onProgress(Math.min(t, 0.98));
                t += l;
                __checkAborted();
                await FJUtil.wait(0.1);
            }

            const segments = this._splitTimeList.map(item => ({
                start: FJAudioUtil.timeToSamples(item.startTime, FJUtil.getAudioSampleRate()),
                end: FJAudioUtil.timeToSamples(item.endTime, FJUtil.getAudioSampleRate()),
            }));
            const newAudioData = FJAudioUtil.extractMultiSegments(audioData, segments);
            const wavBuffer = FJAudioUtil.encodeWAV(newAudioData);
            const mimeType = 'audio/wav';

            const blob = new Blob([new DataView(wavBuffer)], {
                type: mimeType,
            });
            onProgress && onProgress(1);
            onComplete(blob, TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO);
        } catch (error) {
            if (error && error.message === 'ignore') {
                // 非当前的输出
            } else {
                onError && onError(error);
            }
        }
    }

    /**
     * @desc 取消导出
     */
    cancelExport() {
        if (this._sourceType === TEXT_BASE_EDITING_SOURCE_TYPE.VIDEO) {
            FJDecoderManager.cancelDecodersForExport();
            FJEncoderManager.cancel();
            this.removeExportCanvas();
        } else if (this._sourceType === TEXT_BASE_EDITING_SOURCE_TYPE.AUDIO) {
            _currentExportId = '';
        }
    }
}

const manager = new FJTextBaseEditingPreviewManager();
export default manager;
